import axios, { AxiosError, type AxiosAdapter, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Prefetch: keep the response for a few seconds after it lands so the real consumer reuses it. */
    warm?: boolean;
  }
}

interface Entry {
  promise: Promise<AxiosResponse>;
  warm: boolean;
  expiresAt: number;
}

/**
 * Wraps an axios adapter so identical GETs (same URL, params and token) share one request:
 * - a GET still in flight is shared with every identical GET that starts meanwhile;
 * - a warmed GET (`{ warm: true }`) stays reusable for `warmTtlMs` after it lands;
 * - any non-GET drops everything, so nothing read before a write is served after it.
 * Failures are never kept. Each caller gets the response or error with its own config, so the
 * 401 refresh interceptor still retries per caller.
 */
export function createSharedGetAdapter(base: AxiosAdapter, warmTtlMs = 3000): AxiosAdapter {
  const shared = new Map<string, Entry>();

  const keyOf = (config: InternalAxiosRequestConfig) =>
    `${String(config.headers?.Authorization ?? '')} ${axios.getUri({ ...config, params: config.params && Object.keys(config.params).length ? config.params : undefined })}`;

  const forCaller = (entry: Entry, config: InternalAxiosRequestConfig) =>
    entry.promise.then(
      (response) => ({ ...response, config }),
      (error: unknown) => {
        if (!axios.isAxiosError(error)) throw error;
        const response = error.response && { ...error.response, config };
        throw new AxiosError(error.message, error.code, config, error.request, response);
      },
    );

  return (config) => {
    if ((config.method ?? 'get').toLowerCase() !== 'get') {
      shared.clear();
      return base(config);
    }

    const key = keyOf(config);
    const hit = shared.get(key);
    if (hit && Date.now() < hit.expiresAt) {
      if (config.warm) hit.warm = true;
      return forCaller(hit, config);
    }

    const entry: Entry = { promise: base(config), warm: !!config.warm, expiresAt: Infinity };
    shared.set(key, entry);
    entry.promise.then(
      () => {
        if (shared.get(key) !== entry) return;
        if (entry.warm) entry.expiresAt = Date.now() + warmTtlMs;
        else shared.delete(key);
      },
      () => {
        if (shared.get(key) === entry) shared.delete(key);
      },
    );
    return forCaller(entry, config);
  };
}
